import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CompanyType } from '@app/models/admin.model';
import { BaseMasterDataResponse } from '@app/models/shared.models';
import { MasterdataService } from '@app/services/admin/masterdata.service';
import { GenericDataService } from '@app/services/generic-data.service';
import { GenericStore } from '@app/stores/generic.store';
import { AddActionButtonComponent } from '@app/ui-components/add-action-button/add-action-button.component';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { LinkCellRendererComponent } from '@app/ui-components/grid-table/link-cell-renderer/link-cell-renderer.component';
import { SwitchCellRendererComponent } from '@app/ui-components/grid-table/switch-cell-renderer/switch-cell-renderer.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColDef, ValueGetterParams } from 'ag-grid-community';

@Component({
  selector: 'app-company-type-list',
  standalone: true,
  imports: [
    CommonModule,
    GridTableComponent,
    TranslatePipe,
    AddActionButtonComponent,
  ],
  templateUrl: './company-type-list.component.html',
  styleUrl: './company-type-list.component.scss',
})
export class CompanyTypeListComponent implements OnInit {
  companyTypes: CompanyType[] = [];
  columns: ColDef[] = [];

  private store = inject(GenericStore);

  constructor(
    private service: GenericDataService,
    private translate: TranslateService,
    private masterdataService: MasterdataService,
    private router: Router
  ) {
    this.translate.onLangChange.subscribe(() =>
      this.buildColumns(this.store.languageEntities())
    );

    effect(() => {
      const languages = this.store.languageEntities();
      this.buildColumns(languages);
    });
  }

  private buildColumns(languages: BaseMasterDataResponse[]): void {
    this.columns = [
      {
        field: 'isActive',
        headerName: '',
        width: 100,
        editable: true,
        sort: 'desc',
        cellRenderer: SwitchCellRendererComponent,
        cellRendererParams: {
          fieldKey: 'isActive',
          onToggle: (row: CompanyType) => this.onActiveToggle(row),
        },
      },
      {
        field: 'code',
        headerName: this.translate.instant('admin.common.code'),
        cellRenderer: LinkCellRendererComponent,
        cellRendererParams: {
          context: {
            baseRoute: '/admin/database/companyType/',
          },
        },
      },
      ...this.store.languageEntities().map(
        (entity) =>
          ({
            field: entity.description,
            valueGetter: (params: ValueGetterParams<any, string>) =>
              params.data.localized[entity.description] || '-',
            headerName: entity.code,
          } as ColDef)
      ),
    ];
  }

  onActiveToggle(row: any) {
    const companyTypeStatus = !row.isActive;

    this.masterdataService
      .activateCompanyType(row, companyTypeStatus)
      .subscribe({
        next: (updated) => {
          row.isActive = updated?.isActive ?? companyTypeStatus;
        },
        error: (err) => {
          console.error('Error updating company type status', err);
        },
      });
  }

  ngOnInit(): void {
    this.buildColumns(this.store.languageEntities());

    this.service.loadCompanyTypesAdmin().subscribe((res) => {
      this.companyTypes = res?.data;
    });
  }

  addElement() {
    this.router.navigate(['/admin', 'database', 'companyType', 'addNew']);
  }
}
